import { Translations } from 'types/Common';
import { FrequencyUnit } from 'types/general';
import { z } from 'zod';
import { UploadedFile } from 'store/files';
import { Comment } from 'store/comments';
import { EmbeddedReactions } from 'store/reactions';
import { SurveyType } from 'store/surveys';
import {
  recognitionsCommentsFiltersSchema,
  recognitionsFiltersSchema,
} from './recognitions.schemas';

export const recognitionApprovalStatuses = [
  'not_required',
  'pending',
  'approved',
  'rejected',
] as const;
export type RecognitionApprovalStatus =
  (typeof recognitionApprovalStatuses)[number];

export const recognitionTypes = [
  'companyToEmployee',
  'employeeToEmployee',
  'surveyReward',
  'trainingReward',
] as const;
export type RecognitionType = (typeof recognitionTypes)[number];

export const recognitionPoints = [
  'points',
  'thanks',
  'kudos',
  'bravo',
  'karma',
  'emoji_candy',
  'emoji_heart',
  'emoji_prayer',
  'emoji_coin',
  'emoji_star',
  'emoji_strawberry',
  'emoji_clap',
  'emoji_diamond',
  'emoji_unicorn',
  'emoji_panda',
  'emoji_rocket',
  'emoji_wolf',
  'emoji_frog',
  'emoji_green_heart',
  'emoji_truck',
  'emoji_sun',
  'emoji_tornado',
  'emoji_motorway',
  'emoji_seed',
  'emoji_bee',
  'emoji_locomotive',
] as const;
export type RecognitionPoint = (typeof recognitionPoints)[number];

export const recognitionLimits = [
  'none',
  'perEmployee',
  'perDepartment',
  'perCompany',
] as const;
export type RecognitionLimit = (typeof recognitionLimits)[number];

export const expirationNotificationMethods = [
  'email',
  'appNotification',
] as const;
export type ExpirationNotificationMethod =
  (typeof expirationNotificationMethods)[number];

export interface BaseRecognition {
  id: string;
  status: RecognitionApprovalStatus;
  type: RecognitionType;
  pointsReceived: number;
  comment: string;
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
  } | null;
  sender: {
    id: string;
    firstName: string;
    lastName: string;
    fullName: string;
  } | null;
  achievement: {
    id?: string;
    name: string;
  } | null;
  survey: {
    id: string;
    name: string;
    type: SurveyType;
  } | null;
  commentsCount: number;
  reactions: EmbeddedReactions;
  createdAt: string;
}

export interface Recognition extends BaseRecognition {
  image: UploadedFile | null;
  gifId: string;
}

export interface RecognitionsSettings {
  id: string;
  pointRewardPerRecognition: number;
  pointName: RecognitionPoint;
  currencyToPoint: number;
  useMoney: boolean;
  limit: RecognitionLimit;
  limitValue: number;
  limitPeriod: FrequencyUnit | 'none';
  expirationEnabled: boolean;
  expirationDay: number | null;
  expirationMonth: number | null;
  expirationNotificationInDays: number;
  expirationNotification: ExpirationNotificationMethod[];
  showPointsInFeed: boolean;
  allowComments: boolean;
  allowReimbursementClaim: boolean;
  allocationPriority: number;
  suggestionRules: Array<{
    name: string;
    description: string;
    strictness: number;
    enabled: boolean;
  }>;
  translations: Translations<{
    id: string;
    description: string;
    feedName: string;
  }>;
}

export interface RecognitionComment extends Comment {
  recognition: {
    message: string;
    type: RecognitionType;
    sender: {
      id: string;
      fullName: string;
    } | null;
    recipient: {
      id: string;
      fullName: string;
    } | null;
  };
}

export type RecognitionsFilters = z.infer<typeof recognitionsFiltersSchema>;

export type RecognitionsCommentsFilters = z.infer<
  typeof recognitionsCommentsFiltersSchema
>;
